import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    Inject,
    OnDestroy,
    OnInit,
    Optional,
    Renderer2,
    signal,
    ViewChild
} from '@angular/core';
import { AbstractControl, FormControl, FormGroup, NonNullableFormBuilder, ValidatorFn, Validators } from '@angular/forms';
import { DateAdapter } from '@angular/material/core';
import { DateFilterFn } from '@angular/material/datepicker';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
    ContrastAllergyType,
    EntityType,
    GetIntegratedProtoReferral,
    GetIntegratedProtoReferralItem,
    GetReferralDetails,
    GetReferralItem,
    IWorklistItemDetailsDto,
    PatchPatient,
    PatchReferral,
    PostPatient,
    PostReferral,
    PostReferralItem,
    PostReferralPrior,
    PregnancyStatus,
    PutReferralItem,
    SettingType,
    StrokeQueryType,
    UpsertNotification,
    WorklistItemDataSourceType
} from '@app/api-module/control-centre-api-client';
import { DatePickerDateAdapter } from '@app/app-common/adapters/date-picker-date-adapter';
import { MessageBoxButtons, MessageBoxIcon, MessageBoxResult } from '@app/messagebox/messagebox.model';
import { MessageBoxService } from '@app/messagebox/services/messagebox.service';
import { Constants } from '@app/models/constants';
import { ReferralsService } from '@app/referrals/referrals.service';
import { FeaturesService } from '@app/services/features.service';
import { SnackbarService } from '@app/services/snackbar.sevice';
import { CommonUtilities } from '@app/shared/common-utilities';
import { CustomValidators } from '@app/shared/custom-validators';
import { OrgSiteSelectionService } from '@app/shared/org-site-selection/org-site-selection.service';
import { DateTime } from 'luxon';
import {
    BehaviorSubject,
    catchError,
    combineLatest,
    distinctUntilChanged,
    filter,
    firstValueFrom,
    map,
    Observable,
    of,
    startWith,
    Subscription,
    take
} from 'rxjs';
import {
    ContrastAllergyDropdownMapping,
    DefaultDropdownValue,
    DefaultStrokeScanRegions,
    DefaultTraumaScanRegions,
    ImagingRequestFormConstants,
    PositivePhaseOfContrasts,
    PregnancyDropdownMapping,
    RequestCallToggleMapping
} from './constants';
import {
    ContrastAllergyDropdownOption,
    ContrastAllergyMappingKey,
    EditablePrior,
    Egfr,
    ImagingRequestForm,
    PatientForm,
    PatientMedicalHistoryForm,
    PhasesOfContrast,
    PregnancyDropdownOption,
    PregnancyMappingKey,
    RequestedByForm,
    RequestRadiologistCallToggleKey,
    RequestRadiologistCallToggleKeys,
    ScanRegionForm,
    ScanRegionRequest
} from './model';
import { ScanRegionsFormComponent } from './scan-regions-form/scan-regions-form.component';
import { ImagingRequest } from '../models/imaging-requests.model';
import { v4 as uuidv4 } from 'uuid';
import { WorklistService } from '@app/worklist/worklist.service';
import { SettingUtilities } from '@app/shared/setting-utilities';
import { ClientSite } from '@app/models/clientsite';

@Component({
    selector: 'app-imaging-request-form',
    templateUrl: './imaging-request-form.component.html',
    styleUrl: './imaging-request-form.component.scss',
    providers: [{ provide: DateAdapter, useClass: DatePickerDateAdapter }]
})
export class ImagingRequestFormComponent implements AfterViewInit, OnInit, OnDestroy {
    @ViewChild(ScanRegionsFormComponent)
    scanRegionForm: ScanRegionsFormComponent;

    StrokeQueryType = StrokeQueryType;
    ContrastAllergyDropdownMapping = ContrastAllergyDropdownMapping;
    PregnancyDropdownMapping = PregnancyDropdownMapping;
    modalityValues$: Observable<string[]>;
    bodyPartValues$: Observable<string[]>;

    allowedSites: ClientSite[];
    selectedSite: ClientSite;
    siteSelectionEnabled: boolean = false;
    editMode$: Observable<boolean> = of(false);
    showPaediatricConsult$: Observable<boolean>;
    clinicalDiscussionIsMandatory$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    contrastAllergyDropdownOptions: ContrastAllergyDropdownOption[];
    pregnancyDropdownOptions: PregnancyDropdownOption[];

    imagingRequestForm: FormGroup<ImagingRequestForm>;

    // properties to make accessing sub controls easier
    medicalHistoryForm: FormGroup<PatientMedicalHistoryForm>;
    requestedByForm: FormGroup<RequestedByForm>;
    patientForm: FormGroup<PatientForm>;
    saving = false;

    referralDetails: GetReferralDetails | null;
    hasOpenClinicalDiscussion: boolean;

    showSearch$: Observable<boolean>;
    searchEnabled: boolean = false;
    searchForm: FormGroup;
    hasResults: boolean = false;
    mouseOverSearchTermControl = false;
    dataLoadedFromProtoReferral: boolean = false;
    processInitiatedAt: DateTime;

    private readonly _searchTerm$ = new BehaviorSubject<string>('');
    readonly patientSearchResults$ = signal<GetIntegratedProtoReferral[]>([]);
    hasSearchTerm$: Observable<boolean>;

    private readonly _subscriptions = new Subscription();
    private _hubSubscriptionId: string | null = null;
    private _autoPopulateStrokeTraumaScanRegions = new Subscription();

    private readonly _pendingReferralUpdate$: BehaviorSubject<GetReferralDetails | null> = new BehaviorSubject<GetReferralDetails | null>(
        null
    );
    private readonly _pendingDeletedReferralItem$: BehaviorSubject<Set<number>> = new BehaviorSubject<Set<number>>(new Set<number>());
    private readonly _pendingDeletedPriors$: BehaviorSubject<Set<number>> = new BehaviorSubject<Set<number>>(new Set<number>());
    private readonly _pendingOrderUpdates$: BehaviorSubject<IWorklistItemDetailsDto[]> = new BehaviorSubject<IWorklistItemDetailsDto[]>([]);
    hasPendingChanges$: Observable<boolean> = combineLatest([
        this._pendingReferralUpdate$,
        this._pendingDeletedReferralItem$,
        this._pendingDeletedPriors$,
        this._pendingOrderUpdates$
    ]).pipe(
        map(
            ([pendingReferralUpdate, pendingDeletedReferralItems, pendingDeletedPriors, pendingOrderUpdates]) =>
                !!pendingReferralUpdate ||
                pendingDeletedReferralItems.size > 0 ||
                pendingDeletedPriors.size > 0 ||
                pendingOrderUpdates.length > 0
        )
    );

    constructor(
        private readonly formBuilder: NonNullableFormBuilder,
        private readonly orgSiteSelectionService: OrgSiteSelectionService,
        private readonly referralsService: ReferralsService,
        private readonly worklistService: WorklistService,
        private readonly dialogRef: MatDialogRef<ImagingRequestFormComponent>,
        private readonly messageBoxService: MessageBoxService,
        private readonly snackbarService: SnackbarService,
        private readonly featuresService: FeaturesService,
        private readonly renderer: Renderer2,
        private readonly changeDetectorRef: ChangeDetectorRef,
        @Optional() @Inject(MAT_DIALOG_DATA) public imagingRequest: ImagingRequest
    ) {
        this.searchForm = formBuilder.group({
            searchTerm: new FormControl('', [CustomValidators.minTrimmedLengthValidator(3)])
        });

        this.hasSearchTerm$ = this._searchTerm$.pipe(map(searchTerm => !!searchTerm));

        this.showSearch$ = this.orgSiteSelectionService
            .getOrgSelectedSettings$()
            .pipe(
                map(
                    clientSettings =>
                        SettingUtilities.isSettingEnabled(clientSettings, SettingType.IntegratedReferrals) &&
                        !this.imagingRequest?.editableRequest
                )
            );
    }

    ngOnInit(): void {
        this.initialiseForm();
        this.initialiseDropdownOptions();
        this.initialiseSubscriptions();
        this.initialiseSite();

        // this will be done after data is merged to the form in edit mode
        if (!this.imagingRequest?.editableRequest) {
            this.initialiseAutoPopulatingItems();
        }

        this.processInitiatedAt = DateTime.now();
    }

    ngAfterViewInit() {
        // we need to do this here because the ScanRegionsFormComponent
        // viewchild is not guaranteed to exist in ngOnInit
        if (this.imagingRequest?.editableRequest) {
            this._subscriptions.add(
                this.referralsService.getReferral(this.imagingRequest.referralId).subscribe(details => {
                    this.scanRegionForm.removeEmptyScanRegions();
                    this.setupReferralDetails(details);

                    // this prevents changed after checked errors
                    this.changeDetectorRef.detectChanges();

                    // the app toggle component doesn't play nicely with not making changes after the form is initialised
                    this.imagingRequestForm.markAsPristine();

                    // Initialise SignalR updates
                    this.initReferralHubSubscription(details.orgCode, details.siteId);
                })
            );
        }

        setTimeout(() => {
            this.renderer.selectRootElement('#patient-first-name').focus();
        }, 500);
    }

    private initialiseForm() {
        this.imagingRequestForm = this.formBuilder.group<ImagingRequestForm>({
            patient: new FormGroup<PatientForm>({
                firstName: this.formBuilder.control('', [
                    Validators.required,
                    Validators.maxLength(64),
                    Validators.pattern(Constants.NamePattern)
                ]),
                surname: this.formBuilder.control('', [
                    Validators.required,
                    Validators.maxLength(64),
                    Validators.pattern(Constants.NamePattern)
                ]),
                dob: this.formBuilder.control(null, Validators.required),
                location: this.formBuilder.control('', [
                    Validators.required,
                    Validators.maxLength(256),
                    Validators.pattern(Constants.LocationNamePattern),
                    CustomValidators.noWhitespaceValidator
                ]),
                hospitalNumber: this.formBuilder.control('', [
                    Validators.required,
                    Validators.maxLength(64),
                    Validators.pattern(Constants.MrnPattern)
                ]),
                site: this.formBuilder.control(null, Validators.required)
            }),
            requestedBy: new FormGroup<RequestedByForm>({
                clinicianName: this.formBuilder.control('', [
                    Validators.required,
                    Validators.maxLength(64),
                    Validators.pattern(Constants.NamePattern)
                ]),
                clinicianPhoneNumber: this.formBuilder.control('', [
                    Validators.required,
                    Validators.maxLength(32),
                    CustomValidators.noWhitespaceValidator
                ]),
                authorityToRequestScans: this.formBuilder.control(true, Validators.required),
                requestedBy: this.formBuilder.control({ value: '', disabled: true }, [
                    Validators.required,
                    Validators.maxLength(64),
                    Validators.pattern(Constants.NamePattern)
                ])
            }),
            patientMedicalHistory: new FormGroup<PatientMedicalHistoryForm>({
                contrastAllergy: this.formBuilder.control(DefaultDropdownValue, [
                    CustomValidators.noDefaultDropdownOption(DefaultDropdownValue)
                ]),
                pregnancy: this.formBuilder.control(DefaultDropdownValue, [CustomValidators.noDefaultDropdownOption(DefaultDropdownValue)]),
                differentialDiagnosis: this.formBuilder.control('', [Validators.required, CustomValidators.noWhitespaceValidator]),
                clinicalHistory: this.formBuilder.control('', [Validators.required, CustomValidators.noWhitespaceValidator]),
                egfr: this.formBuilder.control('Unknown', [Validators.required]),
                egfrResult: this.formBuilder.control({ value: '', disabled: true }, [
                    Validators.required,
                    Validators.maxLength(32),
                    CustomValidators.noWhitespaceValidator
                ]),
                stroke: this.formBuilder.control(StrokeQueryType.None, Validators.required),
                polyTrauma: this.formBuilder.control(false, Validators.required),
                paediatricConsult: this.formBuilder.control(false, [Validators.required])
            }),
            relevantPriors: this.formBuilder.array<FormGroup<EditablePrior>>([]),
            scanRegions: this.formBuilder.array<FormGroup<ScanRegionForm>>([], Validators.required),
            requestCallWithRadiologist: this.formBuilder.control(false)
        });

        this.medicalHistoryForm = this.imagingRequestForm.controls.patientMedicalHistory;
        this.requestedByForm = this.imagingRequestForm.controls.requestedBy;
        this.patientForm = this.imagingRequestForm.controls.patient;

        // Add initial empty scan region
        this.addEmptyScanRegion();
        this.imagingRequestForm.controls.scanRegions.markAsPristine();

        this.showPaediatricConsult$ = this.patientForm.controls.dob.valueChanges.pipe(map(dob => this.getRequiresPaediatricConsult(dob)));
    }

    private initialiseDropdownOptions() {
        this.contrastAllergyDropdownOptions = Object.values(ContrastAllergyType).map(
            contrastAllergyType =>
                ContrastAllergyDropdownMapping.get(contrastAllergyType) ?? ContrastAllergyDropdownMapping.get(DefaultDropdownValue)
        );
        this.pregnancyDropdownOptions = Object.values(PregnancyStatus).map(
            pregnancyStatus => PregnancyDropdownMapping.get(pregnancyStatus) ?? PregnancyDropdownMapping.get(DefaultDropdownValue)
        );
        this.modalityValues$ = this.referralsService.getModalities().pipe(map(modality => modality.map(m => m.name)));
        this.bodyPartValues$ = this.referralsService.getBodyParts().pipe(map(bodyPart => bodyPart.map(b => b.name)));
    }

    private initialiseSubscriptions() {
        this._subscriptions.add(
            this.requestedByForm.controls.authorityToRequestScans.valueChanges.pipe(distinctUntilChanged()).subscribe(setToTrue => {
                this.updateControlValidity(this.requestedByForm.controls.requestedBy, !setToTrue);
            })
        );
        this._subscriptions.add(
            this.medicalHistoryForm.controls.egfr.valueChanges.pipe(distinctUntilChanged()).subscribe(egfrValue => {
                this.updateControlValidity(this.medicalHistoryForm.controls.egfrResult, egfrValue === 'Known');
            })
        );

        this._subscriptions.add(
            this.patientForm.controls.dob.valueChanges
                .pipe(distinctUntilChanged())
                .subscribe(dob =>
                    this.updateControlValidity(this.medicalHistoryForm.controls.paediatricConsult, this.getRequiresPaediatricConsult(dob))
                )
        );

        this._subscriptions.add(
            combineLatest([
                this.showPaediatricConsult$.pipe(startWith(false)),
                this.medicalHistoryForm.controls.paediatricConsult.valueChanges.pipe(startWith(false)),
                this.medicalHistoryForm.controls.contrastAllergy.valueChanges.pipe(
                    startWith(this.medicalHistoryForm.value.contrastAllergy)
                ),
                this.imagingRequestForm.controls.scanRegions.valueChanges.pipe(startWith([]))
            ])
                .pipe(
                    map(([isPatientUnder16, isPaediatricApproved, contrastAllergy, regionsForScanning]) =>
                        this.clinicalDiscussionIsMandatory(contrastAllergy, regionsForScanning, isPatientUnder16, isPaediatricApproved)
                    )
                )
                .subscribe(clinicalDiscussionIsMandatory => {
                    this.clinicalDiscussionIsMandatory$.next(clinicalDiscussionIsMandatory);
                    this.updateControlValidity(this.imagingRequestForm.controls.requestCallWithRadiologist, !clinicalDiscussionIsMandatory);
                })
        );

        this._subscriptions.add(this.showSearch$.subscribe(shouldShowSearch => (this.searchEnabled = shouldShowSearch)));
    }

    private initialiseAutoPopulatingItems() {
        this._subscriptions.add(
            this.imagingRequestForm.controls.patientMedicalHistory.controls.polyTrauma.valueChanges.subscribe(newTrauma =>
                this.onTraumaChange(newTrauma)
            )
        );
        this._subscriptions.add(
            this.imagingRequestForm.controls.patientMedicalHistory.controls.stroke.valueChanges.subscribe(newStroke =>
                this.onStrokeChanged(newStroke)
            )
        );
    }

    private initialiseSite(): void {
        const selectedSiteIds = this.orgSiteSelectionService.getSelectedSitesValue();
        let selectedSiteId: number | null;
        if (!selectedSiteIds || selectedSiteIds.length === 0) {
            selectedSiteId = null;
        } else if (selectedSiteIds.length === 1) {
            // -1 is a special case - we have selected 'all' sites, so don't use the favourite site, but the first site
            selectedSiteId = selectedSiteIds[0] ?? -1;
        } else {
            selectedSiteId = null;
        }

        this._subscriptions.add(
            this.orgSiteSelectionService
                .getAllowedSites()
                .pipe(
                    filter(([allowedSites, _defaultSiteId]) => allowedSites && allowedSites.length > 0),
                    take(1)
                )
                .subscribe(([allowedSites, defaultSiteId]) => {
                    if (!selectedSiteId) {
                        selectedSiteId = defaultSiteId || allowedSites[0].siteId;
                    }
                    this.siteSelectionEnabled = allowedSites.length > 1 && this.imagingRequest === null;
                    this.allowedSites = allowedSites;
                    let site = null;
                    if (!this.siteSelectionEnabled) {
                        site = allowedSites.find(site => site.siteId === selectedSiteId) || allowedSites[0];
                    }
                    this.selectedSite = site;
                    this.patientForm.controls.site.setValue(site);
                })
        );
    }

    private async initReferralHubSubscription(orgCode: string, siteId: number): Promise<void> {
        this._hubSubscriptionId = uuidv4();

        try {
            await this.referralsService.subscribeToReferralUpdates(
                this._hubSubscriptionId,
                orgCode,
                [siteId],
                upsertNotification => this.handleUpsertedReferral(upsertNotification, orgCode, siteId),
                (entityId, entityType) => this.handleDeletedReferralEntity(entityId, entityType)
            );
        } catch (error) {
            console.warn(error);
        }
    }

    private handleUpsertedReferral(upsertNotification: UpsertNotification, orgCode: string, siteId: number): void {
        if (upsertNotification.baseEntityType !== EntityType.Referral) {
            // we should only care about referrals
            return;
        }

        if (upsertNotification.orgCode !== orgCode || upsertNotification.siteId !== siteId) {
            // we only care about updates relevant to the current org code/site id
            console.warn(
                'Received unexpected upsert notification for org/site not equalling selected org/site',
                upsertNotification.orgCode,
                orgCode,
                siteId,
                upsertNotification.siteId
            );
            return;
        }

        const updatedReferral = upsertNotification.updatedEntityDetails as GetReferralDetails;

        // update is for a different referral
        if (updatedReferral.id !== this.imagingRequest.referralId) {
            return;
        }

        if (upsertNotification.updatedEntityType === EntityType.LinkedOrder) {
            // order(/s) were linked to this referral
            updatedReferral.linkedOrders?.forEach(linkedOrder => {
                const orderAccession = linkedOrder.accessionNumber.toUpperCase();
                this._subscriptions.add(
                    this.worklistService
                        .get(orderAccession, WorklistItemDataSourceType.Order)
                        .pipe(catchError(error => CommonUtilities.getErrorMessage(error)))
                        .subscribe({
                            next: orderDetail => {
                                const pendingOrderUpdates = this._pendingOrderUpdates$.value;
                                pendingOrderUpdates.push(orderDetail);
                                this._pendingOrderUpdates$.next(pendingOrderUpdates);
                            },
                            error: error => this.snackbarService.openSnackBar(error, SnackbarService.SNACK_BAR_CLASS_ALERT)
                        })
                );
            });
        }

        const currentPendingReferralUpdate = this._pendingReferralUpdate$.value;
        const isMoreRecent =
            !currentPendingReferralUpdate ||
            CommonUtilities.dateTimeIsEqualOrMoreRecent(updatedReferral.updatedAt, currentPendingReferralUpdate.updatedAt);

        if (isMoreRecent) {
            this._pendingReferralUpdate$.next(updatedReferral);
        }
    }

    private handleDeletedReferralEntity(entityId: string, entityType: EntityType): void {
        switch (entityType) {
            case EntityType.Referral: {
                const referralId = Number.parseInt(entityId);
                if (referralId && referralId === this.imagingRequest?.referralId) {
                    this.snackbarService.openSnackBar(
                        'The referral for this imaging request was moved to a different site or deleted.',
                        SnackbarService.SNACK_BAR_CLASS_ALERT
                    );
                    this.close();
                }
                break;
            }
            case EntityType.ReferralItem: {
                const referralItemId = this.hasScanRegion(entityId, this.referralDetails.referralItems);
                const referralItemsToDelete = this._pendingDeletedReferralItem$.value;
                if (referralItemId && this.canDeleteScanRegion(referralItemId, referralItemsToDelete)) {
                    this._pendingDeletedReferralItem$.next(referralItemsToDelete);
                }
                break;
            }
            case EntityType.Prior: {
                const priorId = Number.parseInt(entityId);
                if (priorId) {
                    const matchingPrior = this.referralDetails.priors.find(prior => prior.id === priorId);
                    const deletedPriorIds = this._pendingDeletedPriors$.value;
                    if (matchingPrior && !deletedPriorIds.has(priorId)) {
                        deletedPriorIds.add(priorId);
                        this._pendingDeletedPriors$.next(deletedPriorIds);
                    }
                }
                break;
            }
            case EntityType.DecisionItem:
            case EntityType.LinkedOrder:
            default:
                // Ignore others.
                break;
        }
    }

    private hasScanRegion(entityId: string, scanRegions: GetReferralItem[]): number | null {
        const itemId = Number.parseInt(entityId);
        if (itemId && scanRegions?.length > 0) {
            return scanRegions.find(scanRegion => scanRegion.id === itemId) ? itemId : null;
        }
        return null;
    }

    private canDeleteScanRegion(itemId: number, scanRegionIdsToDelete: Set<number>): boolean {
        if (!scanRegionIdsToDelete.has(itemId)) {
            scanRegionIdsToDelete.add(itemId);
            return true;
        }
        return false;
    }

    mergePendingChanges(): void {
        /*
         * Note - in the rare event that an order is linked and then unlinked without changes merged in between
         * we can't handle that and data will be merged as if the order was still linked.
         * This is because linked order deletion only gets us an entity id and the time
         */
        const referralDetails = this._pendingReferralUpdate$.value;
        const orders = this._pendingOrderUpdates$.value;
        if (referralDetails || orders.length > 0) {
            const referral = referralDetails || this.referralDetails;
            const mergeResult = this.imagingRequest.mergeWithChanges(referral, orders, null);
            if (mergeResult.referralChanged) {
                if (!mergeResult.imagingRequest.editableRequest) {
                    // this is no longer an editable request
                    // close this dialog and tell the parent to open a read only equivalent
                    this.dialogRef.close({ reopen: true, data: mergeResult.imagingRequest });

                    // note we have to call return or the rest of this function
                    return;
                }

                this.scanRegionForm.scanRegionControlsArray.clear();
                this.imagingRequestForm.controls.relevantPriors.clear();

                this.setupReferralDetails(referralDetails);

                // this prevents changed after checked errors
                this.changeDetectorRef.detectChanges();

                // the app toggle component doesn't play nicely with not making changes after the form is initialised
                this.imagingRequestForm.markAsPristine();
            }
        }

        this._pendingReferralUpdate$.next(null);

        // Set deleted referral/decision items
        this.deleteScanRegions(this._pendingDeletedReferralItem$);

        // Set deleted priors
        const pendingDeletedPriors = this._pendingDeletedPriors$.value;
        if (pendingDeletedPriors.size > 0) {
            pendingDeletedPriors.forEach(deletedPriorId => {
                const indexOfPriorToDelete = this.imagingRequestForm.controls.relevantPriors.controls.findIndex(
                    prior => prior.value.id === deletedPriorId
                );
                if (indexOfPriorToDelete >= 0) {
                    this.imagingRequestForm.controls.relevantPriors.removeAt(indexOfPriorToDelete);
                }
            });

            this._pendingDeletedPriors$.next(new Set<number>());
        }
    }

    private deleteScanRegions(pendingDeletedScanRegionIds$: BehaviorSubject<Set<number>>) {
        const pendingDeletedScanRegionIds = pendingDeletedScanRegionIds$.value;
        if (pendingDeletedScanRegionIds.size > 0) {
            pendingDeletedScanRegionIds.forEach(scanRegionId => {
                const indexOfMatchingScanRegion = this.imagingRequestForm.controls.scanRegions.controls.findIndex(
                    scanRegion => scanRegion.value.id === scanRegionId
                );
                if (indexOfMatchingScanRegion >= 0) {
                    this.scanRegionForm.deleteScanRegion(indexOfMatchingScanRegion);
                }
            });
            pendingDeletedScanRegionIds$.next(new Set<number>());
            this.scanRegionForm.scanRegionControlsArray.markAsPristine();
        }
    }

    strokeCheckboxUpdated(strokeCheckboxChecked: boolean) {
        const strokeQueryTypeEnabled = this.featuresService.isFeatureEnabled(
            'strokeQueryType',
            this.orgSiteSelectionService.getOrgSelectedValue().countryCode
        );
        let strokeQueryType = StrokeQueryType.None;
        if (strokeCheckboxChecked) {
            strokeQueryType = strokeQueryTypeEnabled ? null : StrokeQueryType.Unspecified;
        }
        this.imagingRequestForm.controls.patientMedicalHistory.controls.stroke.setValue(strokeQueryType);

        if (!strokeCheckboxChecked) {
            if (!this.imagingRequest.editableRequest) {
                // If this is a new referral, mark this as untouched.
                this.imagingRequestForm.controls.patientMedicalHistory.controls.stroke.markAsPristine();
                this.imagingRequestForm.controls.patientMedicalHistory.controls.stroke.markAsUntouched();
            } else {
                this.imagingRequestForm.controls.patientMedicalHistory.controls.stroke.markAsDirty();
            }
        }
    }

    setContrastAllergy(value: ContrastAllergyMappingKey) {
        if (value !== DefaultDropdownValue) {
            const control = this.imagingRequestForm.controls.patientMedicalHistory.controls.contrastAllergy;
            control.setValue(value);
            control.markAsDirty();
        }
    }

    setPregnancy(value: PregnancyMappingKey) {
        if (value !== DefaultDropdownValue) {
            const control = this.imagingRequestForm.controls.patientMedicalHistory.controls.pregnancy;
            control.setValue(value);
            control.markAsDirty();
        }
    }

    selectSite(site: ClientSite) {
        this.selectedSite = site;
        this.patientForm.controls.site.setValue(site);
        this.patientForm.controls.site.markAsDirty();
    }

    addEmptyScanRegion() {
        this.addNewScanRegion('', '', '');
    }

    addNewScanRegion(modality: string, bodyPart: string, phaseOfContrast: string) {
        this.addScanRegion(null, null, modality, bodyPart, phaseOfContrast);
        this.imagingRequestForm.controls.scanRegions.markAsDirty();
    }

    addScanRegion(id: number | null, rowVersion: string | null, modality: string, bodyPart: string, phaseOfContrast: string) {
        this.imagingRequestForm.controls.scanRegions.push(
            this.formBuilder.group<ScanRegionForm>({
                id: this.formBuilder.control(id),
                rowVersion: this.formBuilder.control(rowVersion),
                modality: this.formBuilder.control(modality, Validators.required),
                bodyPart: this.formBuilder.control(bodyPart, Validators.required),
                phaseOfContrast: this.formBuilder.control(phaseOfContrast, Validators.required)
            })
        );
    }

    addEmptyPrior(): void {
        this.addNewPrior('', '', null);
    }

    addNewPrior(modality: string, studyDescription: string, date: Date): void {
        this.addPrior(null, modality, studyDescription, date);
        this.imagingRequestForm.controls.relevantPriors.markAsDirty();
    }

    addPrior(id: number | null, modality: string, studyDescription: string, date: Date): void {
        this.imagingRequestForm.controls.relevantPriors.push(
            this.formBuilder.group<EditablePrior>({
                id: this.formBuilder.control(id),
                modality: this.formBuilder.control(modality, Validators.required),
                studyDescription: this.formBuilder.control(studyDescription, Validators.required),
                studyDate: this.formBuilder.control(date, Validators.required)
            })
        );
    }

    removePrior(priorIndex: number): void {
        this.imagingRequestForm.controls.relevantPriors.removeAt(priorIndex);
        this.imagingRequestForm.controls.relevantPriors.markAsDirty();
    }

    buttonToggled(value: RequestRadiologistCallToggleKey) {
        if (RequestRadiologistCallToggleKeys.includes(value)) {
            this.imagingRequestForm.controls.requestCallWithRadiologist.setValue(RequestCallToggleMapping.get(value));

            // this is a bit of a hack around how the toggle works
            // but we only want this to be dirty when it wasn't set to true from merging a referral to the form
            if (!this.hasOpenClinicalDiscussion) {
                this.imagingRequestForm.controls.requestCallWithRadiologist.markAsDirty();
            }
        } else {
            throw new Error(
                `Toggle used label with undefined type [${value}]. Valid types are [${RequestRadiologistCallToggleKeys.join(', ')}].`
            );
        }
    }

    isDateBeforeToday: DateFilterFn<Date | null> = (date: Date | null): boolean =>
        CommonUtilities.dateTimeIsEqualOrMoreRecent(new Date().toISOString(), date?.toISOString());

    getRequiresPaediatricConsult(dob: Date): boolean {
        if (dob === null) {
            return false;
        }

        const sixteenYearsAgo = new Date();
        sixteenYearsAgo.setFullYear(sixteenYearsAgo.getFullYear() - 16);
        return dob > sixteenYearsAgo;
    }

    onLoadIntegratedProtoReferral(data: { protoReferral: GetIntegratedProtoReferral; selectedItems: GetIntegratedProtoReferralItem[] }) {
        const protoReferral = data.protoReferral;

        // Patient
        this.patientForm.patchValue({
            // first name, last name, and external ID are required properties
            // so we don't have to check if they have values
            firstName: protoReferral.firstName,
            surname: protoReferral.lastName,
            hospitalNumber: protoReferral.patientExternalId,
            location: protoReferral.location || this.patientForm.controls.location.value,
            dob: protoReferral.dateOfBirth ? DateTime.fromISO(protoReferral.dateOfBirth).toJSDate() : this.patientForm.controls.dob.value
        });

        // Requested By
        const requestedByControls = this.requestedByForm.controls;
        this.requestedByForm.patchValue({
            clinicianName: protoReferral.referrerName || requestedByControls.clinicianName.value,
            clinicianPhoneNumber: protoReferral.referrerPhone || requestedByControls.clinicianPhoneNumber.value
        });

        // Medical History
        const medHistControls = this.medicalHistoryForm.controls;
        this.medicalHistoryForm.patchValue({
            clinicalHistory: protoReferral.clinicalHistory || medHistControls.clinicalHistory.value,
            differentialDiagnosis: protoReferral.differentialDiagnosis || medHistControls.differentialDiagnosis.value,
            egfr: this.getEgfrFromProtoReferralOrFormControl(protoReferral, medHistControls.egfr.value),
            egfrResult: protoReferral.egfrResult || medHistControls.egfrResult.value,
            pregnancy: protoReferral.pregnancy || medHistControls.pregnancy.value,
            contrastAllergy: protoReferral.contrastAllergy || medHistControls.contrastAllergy.value
        });

        // Scan Regions
        if (data.selectedItems.length > 0) {
            const validModalities = new Set(this.scanRegionForm.modalities);
            const validBodyParts = new Set(this.scanRegionForm.bodyParts);
            const validPhasesOfContrast = new Set(PhasesOfContrast);

            this.scanRegionForm.deleteAllScanRegions();
            data.selectedItems.forEach(scanRegion => {
                // using null instead of just giving an unrecognised value lets us trip the required validation on the scan region form
                const modality = validModalities.has(scanRegion.modality) ? scanRegion.modality : '';
                const bodyPart = validBodyParts.has(scanRegion.bodyPart) ? scanRegion.bodyPart : '';
                const phaseOfContrast = validPhasesOfContrast.has(scanRegion.phaseOfContrast) ? scanRegion.phaseOfContrast : '';
                if (!this.scanRegionForm.isScanRegionDuplicate(modality, bodyPart, phaseOfContrast, null)) {
                    this.addNewScanRegion(modality, bodyPart, phaseOfContrast);
                }
            });
            this.scanRegionForm.scanRegionControlsArray.markAllAsTouched();
            this.scanRegionForm.refreshRowErrors();
        }

        if (protoReferral.siteName) {
            this.selectSite(new ClientSite(protoReferral.siteId, protoReferral.siteName));
        }

        // mark whole form as touched to highlight what data is missing
        this.imagingRequestForm.markAllAsTouched();

        this.dataLoadedFromProtoReferral = true;
    }

    private getEgfrFromProtoReferralOrFormControl(protoReferral: GetIntegratedProtoReferral, formControlValue: Egfr): Egfr {
        if (protoReferral.egfrResult) {
            return protoReferral.egfrResult !== 'Unknown' ? 'Known' : 'Unknown';
        }

        return formControlValue;
    }

    private onTraumaChange(isTrauma: boolean) {
        if (isTrauma) {
            this.scanRegionForm.removeEmptyScanRegions();
            this.getNonDuplicateScanRegions(DefaultTraumaScanRegions).forEach(scanRegion =>
                this.addNewScanRegion(scanRegion.modality, scanRegion.bodyPart, scanRegion.phaseOfContrast)
            );
        }
    }

    private onStrokeChanged(stroke: StrokeQueryType) {
        switch (stroke) {
            case StrokeQueryType.Unspecified:
            case StrokeQueryType.SimpleStroke:
            case StrokeQueryType.Thrombectomy:
            case StrokeQueryType.Thrombolysis:
                this.scanRegionForm.removeEmptyScanRegions();
                this.getNonDuplicateScanRegions(DefaultStrokeScanRegions).forEach(scanRegion =>
                    this.addNewScanRegion(scanRegion.modality, scanRegion.bodyPart, scanRegion.phaseOfContrast)
                );
                break;
            case StrokeQueryType.None:
            default:
                // Do nothing
                break;
        }
    }

    private getNonDuplicateScanRegions(scanRegions: ScanRegionRequest[] | GetReferralItem[]) {
        return scanRegions.filter(
            scanRegion =>
                !this.scanRegionForm.isScanRegionDuplicate(scanRegion.modality, scanRegion.bodyPart, scanRegion.phaseOfContrast, null)
        );
    }

    async submit() {
        if (this.imagingRequestForm.valid && this.imagingRequestForm.dirty) {
            if (this.imagingRequest?.editableRequest) {
                await this.updateReferral();
            } else {
                await this.submitReferral(this.getPostReferralFromForm());
            }
        } else {
            this.imagingRequestForm.markAllAsTouched();
            this.snackbarService.openSnackBar(
                ImagingRequestFormConstants.SUBMIT_VALIDATION_ERROR_MESSAGE,
                SnackbarService.SNACK_BAR_CLASS_ALERT
            );
        }
    }

    async submitReferral(referral: PostReferral) {
        this.saving = true;
        try {
            await firstValueFrom(this.referralsService.postReferral(referral));
        } catch (err) {
            const errMessage = CommonUtilities.displayErrorMessage(err, ImagingRequestFormConstants.SUMITTED_FALLBACK_ERROR_MESSAGE);
            this.snackbarService.openSnackBar(errMessage, SnackbarService.SNACK_BAR_CLASS_ALERT);
            this.saving = false;
            return;
        }

        const needsClinicalDiscussion =
            this.imagingRequestForm.value.requestCallWithRadiologist === true || this.clinicalDiscussionIsMandatory$.value === true;
        if (needsClinicalDiscussion) {
            const clinicalDiscussionData = this.getClinicalDiscussionNotificationData();

            this._subscriptions.add(
                this.messageBoxService
                    .showMessageBox({
                        ...clinicalDiscussionData,
                        buttons: MessageBoxButtons.Close
                    })
                    .subscribe(() => this.referralCompletedSubmitting())
            );
        } else {
            this.referralCompletedSubmitting();
        }
    }

    private async updateReferral() {
        this.saving = true;
        const clinicalDiscussionRequested = this.imagingRequestForm.value.requestCallWithRadiologist === true;
        const needsClinicalDiscussion = clinicalDiscussionRequested || this.clinicalDiscussionIsMandatory$.value === true;

        if (!this.hasOpenClinicalDiscussion && needsClinicalDiscussion) {
            const clinicalDiscussionData = this.getClinicalDiscussionNotificationData();

            this._subscriptions.add(
                this.messageBoxService
                    .showMessageBox({
                        ...clinicalDiscussionData,
                        textForYes: 'Confirm',
                        textForNo: 'Go Back',
                        buttons: MessageBoxButtons.YesNo
                    })
                    .subscribe(async result => {
                        if (result === MessageBoxResult.Yes) {
                            await this.patchReferral(clinicalDiscussionRequested);
                        } else {
                            this.saving = false;
                        }
                    })
            );
        } else {
            this.patchReferral(false);
        }
    }

    private async patchReferral(clinicalDiscussionRequested: boolean) {
        try {
            const patchReferral = this.getPatchReferralFromForm();
            await firstValueFrom(this.referralsService.patchReferral(this.imagingRequest.referralId, patchReferral));

            if (this.imagingRequestForm.controls.scanRegions.dirty) {
                const putReferralItems = this.getPutReferralItemsFromForm();
                await firstValueFrom(this.referralsService.putReferralItems(this.imagingRequest.referralId, putReferralItems));
            }

            const priorIdsToDelete = this.getPriorIdsToDeleteFromForm();
            if (priorIdsToDelete.length > 0) {
                const priorDeletions = priorIdsToDelete.map(priorId =>
                    firstValueFrom(this.referralsService.deletePrior(this.imagingRequest.referralId, priorId))
                );
                await Promise.all(priorDeletions);
            }

            const postReferralPriors = this.getPostReferralPriorsFromForm();
            if (postReferralPriors.length > 0) {
                await firstValueFrom(this.referralsService.postReferralPriors(this.imagingRequest.referralId, postReferralPriors));
            }
        } catch (err) {
            const errMessage = CommonUtilities.displayErrorMessage(err, ImagingRequestFormConstants.SUMITTED_FALLBACK_ERROR_MESSAGE);
            this.snackbarService.openSnackBar(errMessage, SnackbarService.SNACK_BAR_CLASS_ALERT);
            this.saving = false;
            return;
        }

        if (clinicalDiscussionRequested) {
            await firstValueFrom(this.referralsService.postClinicalDiscussion(this.imagingRequest.referralId));
        }

        this.referralCompletedSubmitting();
    }

    private getClinicalDiscussionNotificationData(): {
        title: string;
        information: string;
        informationLine2: string;
        message: string;
        icon: MessageBoxIcon;
    } {
        let primaryDialogue: string;
        let secondaryDialogue: string = '';
        const edClinicianRequestedClinicalDiscussion = this.clinicalDiscussionIsMandatory$.value !== true;

        if (edClinicianRequestedClinicalDiscussion) {
            primaryDialogue = 'A phone call with an Everlight Radiologist has been requested for this request.';
        } else {
            primaryDialogue = 'A phone call with an Everlight Radiologist is required due to:';

            const paediatricCaseWithoutConsultation =
                this.medicalHistoryForm.value.paediatricConsult !== true && this.getRequiresPaediatricConsult(this.patientForm.value.dob);
            const positiveContrastWithAllergy =
                this.medicalHistoryForm.value.contrastAllergy === ContrastAllergyType.Yes &&
                this.imagingRequestForm.value.scanRegions.some(scanRegion => PositivePhaseOfContrasts.includes(scanRegion.phaseOfContrast));
            if (paediatricCaseWithoutConsultation) {
                secondaryDialogue += 'The patient is under 16 and the scan has not been reviewed with an ED Consultant or Paediatrician. ';
            }
            if (positiveContrastWithAllergy) {
                secondaryDialogue += 'The patient has a contrast allergy and one of the requested scans includes contrast. ';
            }
        }

        return {
            title: ImagingRequestFormConstants.CLINICAL_DISCUSSION_DIALOGUE_MESSAGE,
            information: primaryDialogue,
            informationLine2: secondaryDialogue,
            message: 'You will be contacted shortly by Everlight Radiology.',
            icon: MessageBoxIcon.Information
        };
    }

    getPostReferralFromForm(): PostReferral {
        const referralItems: PostReferralItem[] = this.imagingRequestForm.controls.scanRegions.value.map(
            regionForScan =>
                new PostReferralItem({
                    bodyPart: regionForScan.bodyPart,
                    modality: regionForScan.modality,
                    phaseOfContrast: regionForScan.phaseOfContrast !== 'Unknown' ? regionForScan.phaseOfContrast : null
                })
        );

        const patient = new PostPatient({
            dateOfBirth: this.getDobStringIfValid(this.patientForm.controls.dob),
            firstName: this.patientForm.controls.firstName.value,
            lastName: this.patientForm.controls.surname.value,
            location: this.patientForm.controls.location.value,
            patientExternalId: this.patientForm.controls.hospitalNumber.value
        });

        const priors: PostReferralPrior[] = this.imagingRequestForm.controls.relevantPriors.value.map(
            prior =>
                new PostReferralPrior({
                    bodyPart: prior.studyDescription,
                    modality: prior.modality,
                    priorDate: DateTime.fromJSDate(prior.studyDate).toISODate()
                })
        );

        return new PostReferral({
            patient: patient,
            orgCode: this.orgSiteSelectionService.getOrgSelectedValue().orgCode,
            siteId: this.selectedSite.siteId,
            referrerName: this.requestedByForm.controls.clinicianName.value,
            referrerPhone: this.requestedByForm.controls.clinicianPhoneNumber.value,
            confirmation: this.requestedByForm.controls.authorityToRequestScans.value,
            approvedByDoctor: this.requestedByForm.controls.requestedBy.enabled
                ? this.requestedByForm.controls.requestedBy.value
                : this.requestedByForm.controls.clinicianName.value,
            isTrauma: this.medicalHistoryForm.controls.polyTrauma.value,
            strokeQueryType: this.medicalHistoryForm.controls.stroke.value,
            contrastAllergy: <ContrastAllergyType>this.medicalHistoryForm.controls.contrastAllergy.value,
            pregnancy: <PregnancyStatus>this.medicalHistoryForm.controls.pregnancy.value,
            hasPriorPaediatricConsultation: this.medicalHistoryForm.controls.paediatricConsult.enabled
                ? this.medicalHistoryForm.controls.paediatricConsult.value
                : null,
            clinicalHistory: this.medicalHistoryForm.controls.clinicalHistory.value,
            differentialDiagnosis: this.medicalHistoryForm.controls.differentialDiagnosis.value,
            egfrResult: this.getEgfrResult(),
            referralItems: referralItems,
            priors: priors,
            requestClinicalDiscussion:
                this.imagingRequestForm.controls.requestCallWithRadiologist.value === true &&
                this.clinicalDiscussionIsMandatory$.value !== true,
            autoPopulatedFromIntegratedProtoReferrals: this.searchEnabled ? this.dataLoadedFromProtoReferral : null,
            processInitiatedAt: this.processInitiatedAt.toISO()
        });
    }

    private getPatchReferralFromForm(): PatchReferral {
        const patientControls = this.patientForm.controls;
        const patchPatient = this.patientForm.dirty
            ? new PatchPatient({
                  rowVersion: this.referralDetails.patient.rowVersion,
                  dateOfBirth: patientControls.dob.dirty ? this.getDobStringIfValid(this.patientForm.controls.dob) : null,
                  firstName: this.getValueIfDirty(patientControls.firstName),
                  lastName: this.getValueIfDirty(patientControls.surname),
                  location: this.getValueIfDirty(patientControls.location),
                  patientExternalId: this.getValueIfDirty(patientControls.hospitalNumber)
              })
            : null;

        const requestedByControls = this.requestedByForm.controls;
        const medicalHistoryControls = this.medicalHistoryForm.controls;
        const egfrChanged = medicalHistoryControls.egfrResult.dirty || medicalHistoryControls.egfr.dirty;

        return new PatchReferral({
            rowVersion: this.referralDetails.rowVersion,
            patient: patchPatient,
            referrerName: this.getValueIfDirty(requestedByControls.clinicianName),
            referrerPhone: this.getValueIfDirty(requestedByControls.clinicianPhoneNumber),
            confirmation: this.getValueIfDirty(requestedByControls.authorityToRequestScans),
            approvedByDoctor: this.getPatchReferralApprovedByDoctor(),
            isTrauma: this.getValueIfDirty(medicalHistoryControls.polyTrauma),
            strokeQueryType: this.getValueIfDirty(medicalHistoryControls.stroke),
            contrastAllergy: <ContrastAllergyType>this.getValueIfDirty(medicalHistoryControls.contrastAllergy),
            pregnancy: <PregnancyStatus>this.getValueIfDirty(medicalHistoryControls.pregnancy),
            hasPriorPaediatricConsultation: medicalHistoryControls.paediatricConsult.enabled
                ? this.getValueIfDirty(medicalHistoryControls.paediatricConsult)
                : null,
            clinicalHistory: this.getValueIfDirty(medicalHistoryControls.clinicalHistory),
            differentialDiagnosis: this.getValueIfDirty(medicalHistoryControls.differentialDiagnosis),
            egfrResult: egfrChanged ? this.getEgfrResult() : null
        });
    }

    // We have to make sure we get the value of requested by *always* if they toggle the enabled
    private getPatchReferralApprovedByDoctor(): string {
        const requestedByControls = this.requestedByForm.controls;
        if (requestedByControls.authorityToRequestScans.dirty && requestedByControls.requestedBy.enabled) {
            return requestedByControls.requestedBy.value;
        } else {
            return this.getValueIfDirty(
                requestedByControls.requestedBy.enabled ? requestedByControls.requestedBy : requestedByControls.clinicianName
            );
        }
    }

    private getPutReferralItemsFromForm(): PutReferralItem[] {
        return this.imagingRequestForm.controls.scanRegions.value.map(
            regionForScanning =>
                new PutReferralItem({
                    id: regionForScanning.id,
                    rowVersion: regionForScanning.rowVersion,
                    bodyPart: regionForScanning.bodyPart,
                    modality: regionForScanning.modality,
                    phaseOfContrast: regionForScanning.phaseOfContrast !== 'Unknown' ? regionForScanning.phaseOfContrast : null
                })
        );
    }

    private getPostReferralPriorsFromForm(): PostReferralPrior[] {
        return this.imagingRequestForm.controls.relevantPriors.controls
            .filter(control => control.dirty || control.value.id === null)
            .map(
                prior =>
                    new PostReferralPrior({
                        bodyPart: prior.value.studyDescription,
                        modality: prior.value.modality,
                        priorDate: DateTime.fromJSDate(prior.value.studyDate).toISODate()
                    })
            );
    }

    private getPriorIdsToDeleteFromForm(): number[] {
        const priorIdsInFormToDelete = this.imagingRequestForm.controls.relevantPriors.controls
            .filter(control => this.shouldDeletePrior(control))
            .map(prior => prior.value.id);
        const initialPriorIds = this.referralDetails.priors.map(prior => prior.id);
        const priorIdsInForm = new Set<number>(this.imagingRequestForm.controls.relevantPriors.value.map(prior => prior.id));
        const priorIdsDeletedFromForm = initialPriorIds.filter(id => !priorIdsInForm.has(id));
        return [...priorIdsInFormToDelete, ...priorIdsDeletedFromForm];
    }

    private shouldDeletePrior(priorControl: FormGroup<EditablePrior>): boolean {
        return priorControl.dirty && priorControl.value.id !== null;
    }

    private getValueIfDirty<Type>(control: FormControl<Type>): Type | null {
        return control.dirty ? control.value : null;
    }

    private getDobStringIfValid(dateOfBirthControl: FormControl<Date>): string {
        return dateOfBirthControl.valid ? DateTime.fromJSDate(dateOfBirthControl.value).toISODate() : undefined;
    }

    private getEgfrResult(): string {
        return this.medicalHistoryForm.controls.egfr.value === 'Known' ? this.medicalHistoryForm.controls.egfrResult.value : 'Unknown';
    }

    private clinicalDiscussionIsMandatory(
        contrastAllergy: ContrastAllergyType | 'default',
        regionsForScanning: Partial<ScanRegionRequest>[],
        isPatientUnder16: boolean,
        isPaediatricApproved: boolean
    ) {
        const isContrastAllergy = contrastAllergy === ContrastAllergyType.Yes;
        const hasRegionForScanningAPositiveContrast = regionsForScanning.some((scanRegion: ScanRegionRequest) =>
            PositivePhaseOfContrasts.includes(scanRegion.phaseOfContrast)
        );
        return (isPatientUnder16 && !isPaediatricApproved) || (isContrastAllergy && hasRegionForScanningAPositiveContrast);
    }

    private setupReferralDetails(referralDetails: GetReferralDetails): void {
        this._autoPopulateStrokeTraumaScanRegions.unsubscribe();
        this._autoPopulateStrokeTraumaScanRegions = new Subscription();

        this.referralDetails = referralDetails;

        // Patient
        this.patientForm.patchValue({
            firstName: referralDetails.patient?.firstName,
            surname: referralDetails.patient?.lastName,
            dob: DateTime.fromISO(referralDetails.patient?.dateOfBirth).toJSDate(),
            location: referralDetails.patient?.location,
            hospitalNumber: referralDetails.patient?.patientExternalId
        });

        // Requested By
        this.requestedByForm.patchValue({
            clinicianName: referralDetails.referrerName,
            clinicianPhoneNumber: referralDetails.referrerPhone,
            authorityToRequestScans: referralDetails.confirmation,
            requestedBy: referralDetails.approvedByDoctor
        });

        // Medical History
        this.medicalHistoryForm.patchValue(
            {
                clinicalHistory: referralDetails.clinicalHistory,
                contrastAllergy: referralDetails.contrastAllergy,
                differentialDiagnosis: referralDetails.differentialDiagnosis,
                egfr: referralDetails.egfrResult !== 'Unknown' ? 'Known' : 'Unknown',
                egfrResult: referralDetails.egfrResult,
                paediatricConsult: referralDetails.hasPriorPaediatricConsultation,
                polyTrauma: referralDetails.isTrauma,
                pregnancy: referralDetails.pregnancy,
                stroke: referralDetails.strokeQueryType
            },
            { emitEvent: false }
        );

        // Scan Regions
        if (referralDetails.referralItems) {
            this.getNonDuplicateScanRegions(referralDetails.referralItems).forEach(scanRegion =>
                this.addScanRegion(
                    scanRegion.id,
                    scanRegion.rowVersion,
                    scanRegion.modality,
                    scanRegion.bodyPart,
                    scanRegion.phaseOfContrast || 'Unknown'
                )
            );
        }

        // Priors
        referralDetails.priors?.forEach(prior =>
            this.addPrior(prior.id, prior.modality, prior.bodyPart, DateTime.fromISO(prior.priorDate).toJSDate())
        );

        // Clinical discussion handling, we want to hide the toggle if there is already an open one
        const discussionStatuses = referralDetails.clinicalDiscussions?.map(discussion => discussion.status as unknown as string);
        this.hasOpenClinicalDiscussion = discussionStatuses?.includes('Open') ?? false;

        this.referralDetails = referralDetails;
        this.imagingRequestForm.markAsPristine();
        this.initialiseAutoPopulatingItems();
    }

    private updateControlValidity(formControl: FormControl, enableValidation: boolean) {
        if (enableValidation) {
            formControl.addValidators(Validators.required);
            formControl.enable({ onlySelf: true, emitEvent: false });
        } else {
            formControl.removeValidators(Validators.required);
            formControl.disable({ onlySelf: true, emitEvent: false });
        }
        formControl.updateValueAndValidity();
    }

    clearSearch(): void {
        this.searchForm.controls.searchTerm.patchValue('');
        this._searchTerm$.next('');
    }

    submitSearch(event: UIEvent): void {
        event.preventDefault();
        const searchTermControl = this.searchForm.controls.searchTerm;
        if (searchTermControl.valid) {
            searchTermControl.setValue(searchTermControl.value.trim());
            this._searchTerm$.next(searchTermControl.value);
            this.getPatientSearchResults(searchTermControl.value)
                .pipe(catchError(error => CommonUtilities.getErrorMessage(error)))
                .subscribe({
                    next: results => {
                        this.patientSearchResults$.set(results);
                    },
                    error: error => this.snackbarService.openSnackBar(error, SnackbarService.SNACK_BAR_CLASS_ALERT)
                });
        } else {
            const title = 'More characters are required';
            const message = 'Please enter three or more characters without leading or trailing whitespace to search.';
            this._subscriptions.add(
                this.messageBoxService
                    .showMessageBox({
                        title,
                        message,
                        information: null,
                        icon: MessageBoxIcon.Information,
                        buttons: MessageBoxButtons.Ok
                    })
                    .subscribe()
            );
        }
    }

    getPatientSearchResults(searchTerm: string): Observable<GetIntegratedProtoReferral[]> {
        const orgCode = this.orgSiteSelectionService.getOrgSelectedValue().orgCode;
        const siteIds = this.allowedSites.map(site => site.siteId);
        return this.referralsService.getIntegratedProtoReferrals(orgCode, siteIds, searchTerm);
    }

    private referralCompletedSubmitting(): void {
        this.snackbarService.openSnackBar(ImagingRequestFormConstants.SUBMITTED_SUCCESS_MESSAGE, SnackbarService.SNACK_BAR_CLASS_SUCCESS);
        this.imagingRequestForm.reset();
        this.close();
        this.saving = false;
    }

    onMouseEnterSearchTermControl() {
        this.mouseOverSearchTermControl = true;
    }

    onMouseLeaveSearchTermControl() {
        this.mouseOverSearchTermControl = false;
    }

    close() {
        if (this.imagingRequestForm.dirty) {
            this._subscriptions.add(
                this.messageBoxService
                    .showMessageBox({
                        title: 'Please Confirm',
                        message: 'Are you sure you want to close the request?',
                        information: 'You have not saved the scan request. All details will be lost if you proceed.',
                        icon: MessageBoxIcon.Confirmation,
                        buttons: MessageBoxButtons.YesNo
                    })
                    .subscribe(async result => {
                        if (result === MessageBoxResult.Yes) {
                            this.dialogRef.close({ reopen: false });
                        }
                    })
            );
        } else {
            this.dialogRef.close({ reopen: false });
        }
    }

    private async removeReferralHubSubscription(): Promise<void> {
        if (this._hubSubscriptionId) {
            await this.referralsService.unsubscribeFromReferralUpdates(this._hubSubscriptionId);
            this._hubSubscriptionId = null;
        }
    }

    ngOnDestroy(): void {
        this._autoPopulateStrokeTraumaScanRegions?.unsubscribe();
        this._subscriptions.unsubscribe();
        this.removeReferralHubSubscription();
    }
}
